import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { categories, products } from "../data/products";
import ProductCard from "../components/shop/ProductCard";

export default function Shop() {

  useEffect(() => {
  document.title = "Shop | HD Labs";
}, []);

  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL params
  const urlSearch = (searchParams.get("search") || "").trim();
  const urlCategory = (searchParams.get("category") || "All").trim();
  const urlSort = (searchParams.get("sort") || "featured").trim();

  // Validate category/sort coming from URL
  const safeCategory = categories.includes(urlCategory) ? urlCategory : "All";
  const safeSort = ["featured", "name", "price-low", "price-high"].includes(urlSort)
    ? urlSort
    : "featured";

  // Local UI state
  const [category, setCategory] = useState(safeCategory);
  const [query, setQuery] = useState(urlSearch);
  const [sort, setSort] = useState(safeSort);

  // If URL changes (back/forward OR header navigates), sync state
  useEffect(() => {
    setCategory(safeCategory);
    setQuery(urlSearch);
    setSort(safeSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearch, safeCategory, safeSort]);

  // Whenever user changes search/filter/sort, push it into the URL
  useEffect(() => {
    const next = {};

    if (query.trim()) next.search = query.trim();
    if (category !== "All") next.category = category;
    if (sort !== "featured") next.sort = sort;

    // Only update if something actually changed (prevents loops)
    const currSearch = (searchParams.get("search") || "").trim();
    const currCategory = (searchParams.get("category") || "All").trim();
    const currSort = (searchParams.get("sort") || "featured").trim();

    const nextSearch = (next.search || "").trim();
    const nextCategory = (next.category || "All").trim();
    const nextSort = (next.sort || "featured").trim();

    const changed =
      currSearch !== nextSearch || currCategory !== nextCategory || currSort !== nextSort;

    if (changed) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, category, sort]);

  const filtered = useMemo(() => {
    let list = [...products];

    // ✅ UPDATED: supports products with categories: [] OR legacy category: ""
    if (category !== "All") {
      list = list.filter((p) =>
        Array.isArray(p.categories)
          ? p.categories.includes(category)
          : p.category === category
      );
    }

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }

    if (sort === "price-low") list.sort((a, b) => a.price - b.price);
    if (sort === "price-high") list.sort((a, b) => b.price - a.price);
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));

    return list;
  }, [category, query, sort]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Products</h1>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <input
              className="w-full sm:w-72 rounded-xl border border-slate-200 px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-300"
              placeholder="Search products…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            <select
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-300"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="featured">Featured</option>
              <option value="name">Name A–Z</option>
              <option value="price-low">Price low → high</option>
              <option value="price-high">Price high → low</option>
            </select>
          </div>
        </div>

        <div className="mt-8 grid lg:grid-cols-4 gap-6">
          {/* Filters */}
          <aside className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="text-sm font-medium text-slate-900">Filters</div>
              <div className="mt-4 space-y-2">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${
                      category === c
                        ? "bg-slate-900 text-white"
                        : "bg-slate-50 hover:bg-slate-100 text-slate-700"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Grid */}
          <main className="lg:col-span-3">
            <div className="mb-4 text-sm text-slate-600">
              Showing <span className="font-medium text-slate-900">{filtered.length}</span>{" "}
              product(s)
              {query.trim() ? (
                <>
                  {" "}
                  for <span className="font-medium text-slate-900">“{query.trim()}”</span>
                </>
              ) : null}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
